import { DataTable } from "@/components/DataTable/dataTable.component";
import TranslatedErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import NoteHoverCard from "@/components/NoteHoverCard/noteHoverCard.component";
import FiltersQuoteTracking from "@/components/QuoteTrackingTable/filtersQuoteTracking.component";
import SingleQuoteTable from "@/components/QuoteTrackingTable/singleQuoteTable.component";
import { fetchAllUsersQuotes } from "@/models/socket/quotes.model";
import { Button } from "@/shadcn/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { SimpleQuotation } from "@/types";
import { formatDatePretty } from "@/utils/utils.util";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, Eye } from "lucide-react";
import React, { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useLocation } from "react-router-dom";

type QuoteTrackingTableProps = {
  // TODO: add props here
};

const QuoteTrackingTable: React.FC<QuoteTrackingTableProps> = (props) => {
  const location = useLocation();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<SimpleQuotation[]>([]);
  const [viewingQuoteID, setViewingQuoteID] = useState<string | null>(null);

  const [filteredQuotes, setFilteredQuotes] = useState<SimpleQuotation[]>([]);

  const navFilter = location.state?.filter;

  const [filterStatus, setFilterStatus] = useState<
    "all" | SimpleQuotation["status"]
  >(navFilter?.status ? navFilter.status : "all");
  const [filterCreatedAt, setFilterCreatedAt] = useState<DateRange | undefined>(
    undefined
  );
  const [filterCompany, setFilterCompany] = useState<string>("all");
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);

  async function fetchQuotesData() {
    setLoading(true);
    setError(null);
    setAvailableCompanies([]);
    console.log("Fetching quotes...");
    const response = await fetchAllUsersQuotes();
    console.log(response);
    if (response.rStatus === "success") {
      setQuotes(response.rData);
      const companies = Array.from(
        new Set(response.rData.map((quote) => quote.company_name))
      ).sort();
      setAvailableCompanies(companies);
    } else {
      setError(response.rData);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchQuotesData();
  }, []);

  const columns: ColumnDef<SimpleQuotation>[] = [
    {
      header: "View",
      accessorKey: "view",
      cell: ({ row }) => {
        // Add view action at the end for user convenience
        return (
          <div
            onClick={() => {
              setViewingQuoteID(row.original.id);
            }}
            aria-label={"View"}
          >
            <Eye className="h-5 cursor-pointer" />
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
      // Fixed width constraint for consistent layout
      size: 40,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quote Number
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Quote Number",
      accessorKey: "quote_number",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      // Show quote number as is for user reference
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Customer Name
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Customer Name",
      accessorKey: "company_name",
      enableSorting: true,
      enableHiding: false,
      size: 200,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Account Number
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "account_number",
      enableSorting: true,
      enableHiding: false,
      size: 160,
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Customer ID
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Customer ID",
      accessorKey: "branch_id",
      enableSorting: true,
      enableHiding: false,
      size: 110,
      // Show branch_id as customer ID for traceability
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    // {
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       className="!bg-transparent !p-0"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //     >
    //       Customer Reference
    //       <ArrowUpDown />
    //     </Button>
    //   ),
    //   //   header: "Customer Reference",
    //   accessorKey: "customer_reference",
    //   enableSorting: true,
    //   enableHiding: false,
    //   // Show customer reference or N/A for completeness
    //   cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    // },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quoted By
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Quoted By",
      accessorKey: "quoted_by_name",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      // Show quoted_by_name for accountability
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          VDS Job Number
          <ArrowUpDown />
        </Button>
      ),
      //   header: "VDS Job Number",
      accessorKey: "vds_job_number",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      // Show vds_job_number for job tracking
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Account Expiry Date
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Account Expiry Date",
      accessorKey: "quote_expiry_at",
      enableSorting: true,
      enableHiding: false,
      size: 160,
      // Format expiry date for user readability
      cell: ({ cell }) =>
        cell.getValue() ? (
          <span>
            {formatDatePretty(cell.getValue() as string, false, false)}
          </span>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quote Creation Date
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Quote Created Date",
      accessorKey: "quote_created_at",
      enableSorting: true,
      enableHiding: false,
      size: 170,
      // Format created date for user readability
      cell: ({ cell }) =>
        cell.getValue() ? (
          <span>
            {formatDatePretty(cell.getValue() as string, true, false)}
          </span>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Last Modified Date
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Last Modified Date",
      accessorKey: "quote_updated_at",
      enableSorting: true,
      enableHiding: false,
      size: 160,
      // Format updated date for user readability
      cell: ({ cell }) =>
        cell.getValue() ? (
          <span>
            {formatDatePretty(cell.getValue() as string, true, false)}
          </span>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quote Expiry Date
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Quote Expiry Date",
      accessorKey: "quote_expiry_at",
      enableSorting: true,
      enableHiding: false,
      size: 150,
      // Repeat expiry date for explicitness as per requirements
      cell: ({ row }) => {
        // We use a block here to allow variable declarations for clarity
        const isExpired = row.original.status === "expired";
        const cell = row.getValue("quote_expiry_at");
        return cell ? (
          <span className={isExpired ? "text-red-500" : ""}>
            {formatDatePretty(cell as string, true, false)}
          </span>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quote Status
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Quote Status",
      accessorKey: "status",
      enableSorting: true,
      enableHiding: false,
      size: 120,
      // Show status as is for clarity
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <TooltipProvider>
            <Tooltip delayDuration={150}>
              <TooltipTrigger>
                <div className="">
                  <div
                    className={`rounded p-1 capitalize ${status} ${
                      status === "draft"
                        ? "bg-primary text-black"
                        : status === "open"
                          ? "bg-quote-open text-white"
                          : status === "accepted"
                            ? "bg-quote-accepted text-white"
                            : status === "rejected"
                              ? "bg-quote-rejected text-white"
                              : status === "expired"
                                ? "bg-quote-expired text-black"
                                : "bg-primary text-black"
                    } `}
                  >
                    {String(status || "N/A")}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {status === "draft"
                    ? "Your quote has been created but pricing still being validated. Quote will display without values"
                    : status === "open"
                      ? "Your quote has been created and sent. We're waiting for your response or action."
                      : status === "accepted"
                        ? "Accepted - You've approved the quote. We'll proceed with the next steps."
                        : status === "rejected"
                          ? "Rejected - You've declined the quote. No further action will be taken."
                          : status === "expired"
                            ? "Expired - This quote was open for more than 30 days and is now no longer valid."
                            : "Unknown status"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
      // No need for filterFn here since we filter before passing data
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Approval Status
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Approval Status",
      accessorKey: "quote_approval_at",
      enableSorting: true,
      enableHiding: false,
      size: 140,
      // Show approval date or N/A to indicate approval status
      cell: ({ cell }) =>
        cell.getValue() ? (
          <span>
            {formatDatePretty(cell.getValue() as string, true, false)}
          </span>
        ) : (
          <span>N/A</span>
        ),
    },
    // quote_notes
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Note
          <ArrowUpDown />
        </Button>
      ),
      accessorKey: "note",
      enableSorting: true,
      enableHiding: false,
      size: 80,
      cell: ({ cell }) =>
        cell.getValue() ? (
          <NoteHoverCard note={cell.getValue() as string} />
        ) : null,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Total (Excl. VAT)
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Total (Excl. VAT)",
      accessorKey: "total_excl_vat",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      // Show total excluding VAT for financial clarity
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          VAT Amount
          <ArrowUpDown />
        </Button>
      ),
      //   header: "VAT Amount",
      accessorKey: "vat_amount",
      enableSorting: true,
      enableHiding: false,
      size: 120,
      // Show VAT amount for financial clarity
      cell: ({ cell }) => (
        <span>
          {cell.getValue() != null ? `${String(cell.getValue())}%` : "N/A"}
        </span>
      ),
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Total (Incl. VAT)
          <ArrowUpDown />
        </Button>
      ),
      //   header: "Total (Incl. VAT)",
      accessorKey: "total_incl_vat",
      enableSorting: true,
      enableHiding: false,
      size: 130,
      // Show total including VAT for financial clarity
      cell: ({ cell }) => <span>{String(cell.getValue() || "N/A")}</span>,
    },
    {
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="!bg-transparent !p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          File Attached
          <ArrowUpDown />
        </Button>
      ),
      //   header: "File Attached",
      accessorKey: "file_id",
      enableSorting: false,
      enableHiding: false,
      size: 100,
      // Show if file is attached for user awareness, yes/no
      cell: ({ cell }) => (cell.getValue() ? "Yes" : "No"),
    },
  ];

  //   type DateRange = {
  //     from: Date | undefined;
  //     to?: Date;
  // }
  useEffect(() => {
    if (!quotes || quotes.length === 0) {
      setFilteredQuotes([]);
      return;
    }

    // Filter quotes based on status and created date range for user-driven filtering
    const filtered = quotes.filter((quote) => {
      // Status filter
      if (filterStatus !== "all" && quote.status !== filterStatus) return false;
      // Created date range filter
      if (filterCreatedAt && filterCreatedAt.from) {
        const createdAt = new Date(quote.quote_created_at);
        const from = filterCreatedAt.from;
        // If 'to' is not set, treat as single day
        const to = filterCreatedAt.to
          ? filterCreatedAt.to
          : filterCreatedAt.from;
        // Compare only date part for inclusiveness
        if (
          createdAt < new Date(from.setHours(0, 0, 0, 0)) ||
          createdAt > new Date(to.setHours(23, 59, 59, 999))
        ) {
          return false;
        }
      }

      // Company filter
      if (filterCompany && filterCompany !== "all") {
        return quote.company_name === filterCompany;
      }
      return true;
    });
    setFilteredQuotes(filtered);
  }, [quotes, filterStatus, filterCreatedAt, filterCompany]);

  function resetTableFilters() {
    setFilterStatus("all");
    setFilterCreatedAt(undefined);
    setFilterCompany("all");
  }

  useEffect(() => {
    // if the filters change then close the single view
    setViewingQuoteID(null);
  }, [filterStatus, filterCreatedAt, filterCompany]);

  return loading ? (
    <div className="py-4">
      <div className="flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
      </div>
      <div className="flex items-center justify-center">
        <h2 className="mt-2 text-xl text-white">Loading Quotes...</h2>
      </div>
    </div>
  ) : error ? (
    <div className="flex items-center justify-center">
      <p className="text-red-500">{error}</p>
    </div>
  ) : (
    <div className="mx-4">
      <DataTable
        columns={columns}
        data={filteredQuotes} // Pass filtered data
        includeSelectRow={false}
        disableColumnSelection={true}
        // Set initial sort to "quote_created_at" descending so newest quotes show first
        initialState={{
          sorting: [
            {
              id: "quote_created_at",
              desc: true,
            },
          ],
        }}
        columnFiltersUI={
          <FiltersQuoteTracking
            status={filterStatus}
            setStatus={setFilterStatus}
            // Pass created date filter to filters UI for user control
            createdAt={filterCreatedAt}
            setCreatedAt={setFilterCreatedAt}
            company={filterCompany}
            setCompany={setFilterCompany}
            availableCompanies={availableCompanies} // Pass available companies for filtering
          />
        } // Pass filter UI
        resetTableFilters={resetTableFilters} // Pass reset function
        resetFiltersDisabled={
          filterStatus === "all" && !filterCreatedAt && filterCompany === "all"
        }
      />
      {viewingQuoteID && (
        <TranslatedErrorBoundary>
          <SingleQuoteTable
            quoteID={viewingQuoteID}
            closeTable={() => setViewingQuoteID(null)}
            refetchQuotesData={fetchQuotesData}
          />
        </TranslatedErrorBoundary>
      )}
    </div>
  );
};

export default QuoteTrackingTable;
